import React, {
  Dispatch,
  useCallback,
  useContext,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
  SetStateAction,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { Field, Form } from 'react-final-form';
import QuestionRichTextEditor from 'router/subrouters/DataRoom/components/QuestionRichTextEditor';
import AnswerRichTextEditor from 'router/subrouters/DataRoom/components/AnswerRichTextEditor';
import InputField from 'components/InputField';
import validations from 'validations';
import { Button } from 'ncoded-component-library';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ChooseAFileInDr from 'router/subrouters/DataRoom/components/ChooseAFileInDr';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import useCallbackRef from 'hooks/useCallbackRef';
import useQueryParams from 'hooks/useQueryParams';
import { Question } from 'router/subrouters/DataRoom/models/QAndA';
import { FAQ_PAGE_LIMIT } from '../../providers/Faq/Faq.provider';
import SelectField from 'components/SelectField';
import useHeightAnimation from 'hooks/useHeightAnimation';
import { FAQAccess } from 'router/subrouters/DataRoom/components/FAQAccess';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  getFAQAccess,
  processFAQAccess,
} from 'router/subrouters/DataRoom/components/FAQAccess/utils';
import { DATA_ROOM_FAQ_ACCESS } from 'router/subrouters/DataRoom/models/FAQ';
import useQuestionSubjects from 'router/subrouters/DataRoom/hooks/useQuestionSubjects';
import { FIRST_PAGE, OPEN_FAQ_DRAWER_PARAM } from 'data/global';
import arrayMutators from 'final-form-arrays';

import './CreateFaqDrawer.styles.scss';

type CreateFaqDrawerProps = {
  className?: string;
  resetPageOnCreate?: boolean;
  onSuccess?: (question: Question) => void;
  setDisplayedQuestions?: Dispatch<SetStateAction<Question[]>>;
  setQuestionsLoading?: (value: boolean) => void;
  updateQuestion?: (question: Partial<Question>) => void;
};

export type CreateFaqRef = SideDrawerRef & {
  openForEdit: (question: Question) => void;
};

const CreateFaqDrawer: React.ForwardRefRenderFunction<
  CreateFaqRef,
  CreateFaqDrawerProps
> = (props, ref) => {
  const {
    className,
    resetPageOnCreate = false,
    onSuccess,
    setDisplayedQuestions,
    setQuestionsLoading,
    updateQuestion,
  } = props;
  const { t } = useTranslation();
  const {
    faqAccessData,
    questionSubjects,
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const {
    params: { page },
    setQueryParam,
    removeQueryParam,
  } = useQueryParams();
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const [nodeToRelateTo, setNodeToRelateTo] = useState<DataRoomNode>(null);
  const chooseAFileInDrRef = useRef(null);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const [questionForEdit, setQuestionForEdit] = useState<Question | null>(null);
  const [container, containerRef] = useCallbackRef<HTMLDivElement>();

  const { questionSubjectsOptions } = useQuestionSubjects(questionSubjects);

  const classes = classNames('create-faq-drawer', className);

  useHeightAnimation({
    element: container,
  });

  useImperativeHandle(
    ref,
    () => ({
      ...drawer,
      openForEdit: (question: Question) => {
        setQuestionForEdit(question);
        drawer?.setOpen?.(true);
      },
    }),
    [drawer],
  );

  const onFaqSubmit = useCallback(
    async (values, form) => {
      try {
        setQuestionsLoading?.(true);

        const newValues = processFAQAccess(values, faqAccessData);

        const { data } = questionForEdit
          ? await dataRoomApi.faqs.updateFaq(
              questionForEdit._id,
              dataRoomId,
              { ...questionForEdit, ...newValues },
              nodeToRelateTo?._id,
            )
          : await dataRoomApi.faqs.createFaq(
              dataRoomId,
              newValues,
              nodeToRelateTo?._id,
            );

        if (resetPageOnCreate && !questionForEdit && +page !== 1) {
          setQueryParam('page', FIRST_PAGE);
        } else {
          setDisplayedQuestions?.((old) => {
            const ind = old.findIndex((q) => q._id === data._id);
            const newArr = [...old];

            if (ind !== -1) {
              newArr.splice(ind, 1, data);
              return newArr;
            }
            if (newArr.length >= FAQ_PAGE_LIMIT) {
              newArr.pop();
            }

            return [{ ...data, openDrawerDefaultly: true }, ...newArr];
          });
        }

        setQuestionForEdit(null);
        drawer?.setOpen(false);
        onSuccess?.(data);
        popSuccess(t(questionForEdit ? 'updatedFaq' : 'createdFaqSucc'));
        form.reset();
        form.restart();
      } catch (e) {
        popServerError(e);
      } finally {
        setQuestionsLoading?.(false);
      }
    },
    [
      setQuestionsLoading,
      faqAccessData,
      questionForEdit,
      dataRoomId,
      nodeToRelateTo?._id,
      resetPageOnCreate,
      page,
      drawer,
      onSuccess,
      popSuccess,
      t,
      setQueryParam,
      setDisplayedQuestions,
      popServerError,
    ],
  );

  const initialVals = useMemo(
    () =>
      questionForEdit
        ? {
            subject: questionForEdit.subject,
            otherSubject: questionForEdit.otherSubject,
            content: questionForEdit.content,
            answerContent: questionForEdit.dataRoomAnswer.content,
            ...getFAQAccess(questionForEdit),
          }
        : { faqAccess: DATA_ROOM_FAQ_ACCESS.ALL },
    [questionForEdit],
  );

  const onRemoveQuestionFile = useCallback(
    (fileId: string) => {
      setQuestionForEdit((old) => {
        const newQ = {
          ...old,
          files: old.files.filter(({ _id }) => _id !== fileId),
        };
        updateQuestion?.(newQ);

        return newQ;
      });
    },
    [updateQuestion],
  );

  const onRemoveAnswerFile = useCallback(
    (fileId: string) => {
      setQuestionForEdit((old) => {
        const newQ = {
          ...old,
          dataRoomAnswer: {
            ...old.dataRoomAnswer,
            files: old.dataRoomAnswer.files?.filter(
              ({ _id }) => _id !== fileId,
            ),
          },
        };
        updateQuestion?.(newQ);

        return newQ;
      });
    },
    [updateQuestion],
  );

  useEffect(() => {
    if (questionForEdit) {
      questionForEdit.dataRoomNode &&
        setNodeToRelateTo(questionForEdit.dataRoomNode);
    }
  }, [questionForEdit]);

  useEffect(() => {
    if (drawer?.open) removeQueryParam(OPEN_FAQ_DRAWER_PARAM);
  }, [drawer?.open, removeQueryParam]);

  return (
    <SideDrawer
      className={classes}
      ref={drawerRef}
      title={t(questionForEdit ? 'updateQuestion' : 'addCustomFaq')}
      closeAction={() => setQuestionForEdit(null)}
    >
      <Form
        mutators={{ ...arrayMutators }}
        initialValues={initialVals}
        onSubmit={onFaqSubmit}
        render={({ handleSubmit, submitting, invalid, values }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="subject"
              label={t('questionSubject')}
              component={SelectField}
              options={questionSubjectsOptions}
              key={values.subject}
              searchable
              validate={validations.required()}
              required
            />
            {values.subject === 'Other' && (
              <Field
                name="otherSubject"
                label={t('otherSubject')}
                component={InputField}
                maxLength={100}
                validate={validations.required()}
                required
              />
            )}

            <QuestionRichTextEditor
              label={t('writeBriefQuestion')}
              questionId={questionForEdit?._id}
              initialFiles={questionForEdit?.files}
              onRemoveQuestionFile={onRemoveQuestionFile}
            />

            <AnswerRichTextEditor
              label={t('answerTheQuestion')}
              filesName="answerUploadedFiles"
              answerId={questionForEdit?.dataRoomAnswer?._id}
              initialFiles={questionForEdit?.dataRoomAnswer?.files}
              onRemoveAnswerFile={onRemoveAnswerFile}
            />

            <ChooseAFileInDr
              className="qa-ask-question-drawer__file"
              dataRoomId={dataRoomId}
              currenntlySelectedFile={nodeToRelateTo}
              onChange={setNodeToRelateTo}
              ref={chooseAFileInDrRef}
            />

            <div ref={containerRef} className="animation-container">
              <FAQAccess showFAQAccess data={faqAccessData} />
            </div>

            {!questionForEdit && (
              <p className="notify">{t('publishFaqEmailNotifNote')}</p>
            )}

            <div className="create-faq-drawer__actions">
              <Button type="submit" disabled={submitting || invalid}>
                {t(questionForEdit ? 'updateFaqAndPublish' : 'createFaq')}
              </Button>
            </div>
          </form>
        )}
      />
    </SideDrawer>
  );
};

export default forwardRef(CreateFaqDrawer);
